import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { PlusIcon, XMarkIcon, BookOpenIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { logbookApi, type CreateDivePayload } from '@/api/logbook'
import { queueDiveLog, pendingDiveLogCount, OFFLINE_DIVES_CHANGED } from '@/lib/diveLogSync'
import { format } from 'date-fns'

interface QuickForm {
  date: string
  maxDepthM: number
  durationMin: number
  diveSiteName: string
  diveType: string
  rating: number
  notes: string
}

const DIVE_TYPES = [
  { v: 'RECREATIONAL', l: 'Ricreativa' },
  { v: 'TECHNICAL',    l: 'Tecnica' },
  { v: 'FREEDIVING',   l: 'Apnea' },
  { v: 'NIGHT',        l: 'Notturna' },
  { v: 'CAVE',         l: 'Grotta' },
  { v: 'WRECK',        l: 'Relitto' },
  { v: 'DRIFT',        l: 'Deriva' },
  { v: 'TRAINING',     l: 'Training' },
]

export default function QuickLogFAB() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pendingOffline, setPendingOffline] = useState(0)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const today = format(new Date(), 'yyyy-MM-dd')

  // DV-10: qualunque CTA (es. l'hero "log rapido" in dashboard) può aprire il log
  // rapido lanciando l'evento 'edive:quicklog', senza duplicare il form.
  useEffect(() => {
    const openQuick = () => setOpen(true)
    window.addEventListener('edive:quicklog', openQuick)
    return () => window.removeEventListener('edive:quicklog', openQuick)
  }, [])

  // Badge "in attesa di sincronizzazione": immersioni salvate offline non ancora inviate.
  useEffect(() => {
    const refresh = () => { void pendingDiveLogCount().then(setPendingOffline) }
    refresh()
    window.addEventListener(OFFLINE_DIVES_CHANGED, refresh)
    window.addEventListener('online', refresh)
    return () => {
      window.removeEventListener(OFFLINE_DIVES_CHANGED, refresh)
      window.removeEventListener('online', refresh)
    }
  }, [])

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<QuickForm>({
    defaultValues: {
      date: today,
      maxDepthM: 0,
      durationMin: 0,
      diveSiteName: '',
      diveType: 'RECREATIONAL',
      rating: 0,
      notes: '',
    },
  })

  const rating = watch('rating')

  // Ultima immersione: per il caso frequente (ri-immergersi nello stesso sito)
  // basta un tap per riportare sito e tipo, senza riscriverli.
  const { data: lastDiveList } = useQuery({
    queryKey: ['last-dive-quick'],
    queryFn: () => logbookApi.list({ limit: 1 }),
    enabled: open,
    staleTime: 60_000,
  })
  const lastDive = lastDiveList?.dives?.[0]

  function repeatLast() {
    if (!lastDive) return
    if (lastDive.diveSiteName) setValue('diveSiteName', lastDive.diveSiteName)
    if (lastDive.diveType) setValue('diveType', lastDive.diveType)
  }

  function resetForm() {
    reset({ date: today, maxDepthM: 0, durationMin: 0, diveSiteName: '', diveType: 'RECREATIONAL', rating: 0, notes: '' })
  }

  // Salva l'immersione nella coda offline (IndexedDB): verrà inviata al ritorno online.
  async function saveOffline(payload: CreateDivePayload) {
    try {
      await queueDiveLog(payload)
      toast.success(t('quickLog.savedOffline', 'Immersione salvata offline: verrà sincronizzata al ritorno online.'))
      resetForm()
      setOpen(false)
    } catch {
      toast.error(t('auto.componentsQuickLogFAB.erroreNelSalvataggio'))
    }
  }

  const mutation = useMutation({
    mutationFn: (data: CreateDivePayload) => logbookApi.create(data),
    onSuccess: (diveId) => {
      qc.invalidateQueries({ queryKey: ['dives'] })
      qc.invalidateQueries({ queryKey: ['logbook-stats'] })
      toast.success(
        <div>
          <p className="font-medium">{t('auto.componentsQuickLogFAB.immersioneRegistrata')}</p>
          <button
            className="text-ocean-600 text-xs underline mt-0.5"
            onClick={() => navigate(`/app/logbook/${diveId}`)}
          >
            Vedi dettagli →
          </button>
        </div>,
        { duration: 4000 }
      )
      resetForm()
      setOpen(false)
    },
    // Errore SENZA risposta = problema di rete: non si perde l'immersione, va in coda offline.
    // Un errore con risposta (validazione, ecc.) resta un errore mostrato all'utente.
    onError: (err: any, variables) => {
      if (!err?.response) { void saveOffline(variables) }
      else toast.error(err?.response?.data?.error ?? t('auto.componentsQuickLogFAB.erroreNelSalvataggio'))
    },
  })

  const onSubmit = (data: QuickForm) => {
    const payload: CreateDivePayload = {
      date: data.date,
      maxDepthM: Number(data.maxDepthM),
      durationMin: Number(data.durationMin),
      diveType: data.diveType,
    }
    if (data.diveSiteName.trim()) payload.diveSiteName = data.diveSiteName.trim()
    if (data.rating > 0) payload.rating = data.rating
    if (data.notes.trim()) payload.notes = data.notes.trim()
    // Offline conclamato: salta la rete e vai dritto in coda (niente attesa/errore inutile).
    if (typeof navigator !== 'undefined' && !navigator.onLine) { void saveOffline(payload); return }
    mutation.mutate(payload)
  }

  return (
    <>
      {/* FAB button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-30 w-14 h-14 bg-ocean-600 hover:bg-ocean-700 active:scale-95
          text-white rounded-full shadow-lg flex items-center justify-center transition-all
          focus:outline-none focus:ring-4 focus:ring-ocean-500/40"
        title={t('auto.componentsQuickLogFAB.logRapidoImmersione')}
        aria-label={t('auto.componentsQuickLogFAB.registraImmersione')}
      >
        <PlusIcon className="w-7 h-7" />
        {pendingOffline > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white
              text-[11px] font-bold flex items-center justify-center border-2 border-white"
            title={t('quickLog.pendingSync', { defaultValue: '{{count}} in attesa di sincronizzazione', count: pendingOffline })}
          >
            {pendingOffline}
          </span>
        )}
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92dvh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <BookOpenIcon className="w-5 h-5 text-ocean-600" />
                <h2 className="text-base font-semibold text-gray-900">{t('auto.componentsQuickLogFAB.logRapido')}</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-2.5 -mr-1.5 text-gray-400 hover:text-gray-600 active:text-gray-900 rounded-xl transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
              {/* Ripeti ultima immersione: prefill rapido di sito + tipo */}
              {lastDive?.diveSiteName && (
                <button
                  type="button"
                  onClick={repeatLast}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-ocean-200 bg-ocean-50 text-ocean-700 text-sm hover:bg-ocean-100 transition-colors"
                >
                  <ArrowPathIcon className="w-4 h-4 shrink-0" />
                  <span className="truncate">
                    {t('quickLog.repeatLast', 'Ripeti ultima')}: <span className="font-medium">{lastDive.diveSiteName}</span>
                  </span>
                </button>
              )}

              {/* Date */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('auto.componentsQuickLogFAB.data')}</label>
                <input
                  type="date"
                  {...register('date', { required: true })}
                  max={today}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-ocean-500"
                />
              </div>

              {/* Depth + Duration */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Profondità max <span className="text-gray-400 font-normal">(m)</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="500"
                    {...register('maxDepthM', { required: true, min: 0.1, valueAsNumber: true })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none
                      ${errors.maxDepthM ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-ocean-500'}`}
                  />
                  {errors.maxDepthM && <p className="text-xs text-red-500 mt-0.5">{t('auto.componentsQuickLogFAB.inserisciLaProfondita')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Durata <span className="text-gray-400 font-normal">{t('auto.componentsQuickLogFAB.min')}</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="600"
                    {...register('durationMin', { required: true, min: 1, valueAsNumber: true })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none
                      ${errors.durationMin ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-ocean-500'}`}
                  />
                  {errors.durationMin && <p className="text-xs text-red-500 mt-0.5">{t('auto.componentsQuickLogFAB.inserisciLaDurata')}</p>}
                </div>
              </div>

              {/* Site + Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('auto.componentsQuickLogFAB.sito')}</label>
                  <input
                    type="text"
                    {...register('diveSiteName')}
                    placeholder={t('auto.componentsQuickLogFAB.nomeSito')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-ocean-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('auto.componentsQuickLogFAB.tipo')}</label>
                  <select
                    {...register('diveType')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-ocean-500 bg-white"
                  >
                    {DIVE_TYPES.map(t => (
                      <option key={t.v} value={t.v}>{t.l}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('auto.componentsQuickLogFAB.valutazione')}</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setValue('rating', star === rating ? 0 : star)}
                      className={`text-xl transition-colors ${star <= rating ? 'text-amber-400' : 'text-gray-200 hover:text-amber-300'}`}
                    >
                      ★
                    </button>
                  ))}
                  {rating > 0 && (
                    <button
                      type="button"
                      onClick={() => setValue('rating', 0)}
                      className="text-xs text-gray-400 hover:text-gray-600 ml-1 self-center"
                    >
                      rimuovi
                    </button>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('auto.componentsQuickLogFAB.noteOpzionale')}</label>
                <textarea
                  {...register('notes')}
                  rows={2}
                  placeholder={t('auto.componentsQuickLogFAB.condizioniFaunaVistaBuddy')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-ocean-500 resize-none"
                />
              </div>

            </div>
            {/* Sticky submit footer */}
            <div className="shrink-0 px-5 py-3 border-t border-gray-100 flex gap-3"
                 style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 py-3 rounded-xl bg-ocean-600 hover:bg-ocean-700 active:bg-ocean-800 text-white text-sm font-medium
                  transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {mutation.isPending
                  ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {t('auto.componentsQuickLogFAB.salvataggio')}</>
                  : <><BookOpenIcon className="w-4 h-4" /> {t('auto.componentsQuickLogFAB.salvaImmersione')}</>
                }
              </button>
            </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
